import i18next from 'i18next';
import { Updater } from 'widgets-for-react';
import { UserData } from "../../subscription/subscriptionState";
import { buildValidator, isValidatorPassed, Validator, validatorBuilders, ValidatorResult, validatorResultBuilders } from "../../validator/validator";
import { CountryState, initialCountryState } from "../country/countryState";
import { initialJobState, JobState } from "../job/jobState";
import { initialLanguageState, LanguageState } from '../language/languageState';

export type PersonalInfo = Partial<{
  name:string
  surname:string
  // email:string
  gender:"male"|"female"|"other"
  phoneNumber:string
  dateOfBirth:Date
  // address:string
  // postCode:string
  // city:string
  graduationYear: number
}>

type PersonalInfoPrimitives = Pick<PersonalInfo, Exclude<keyof PersonalInfo, "gender">> & { gender?:string }

export const availableGenders:Array<PersonalInfo["gender"]> =  ["male", "female", "other"]
export const emptyPersonalInfo : PersonalInfo = {}

const personalInfoValidator = buildValidator<PersonalInfoPrimitives>({
  name:[b => b.notEmpty(), validatorBuilders.string("name", () => i18next.t(`subscriptionValidators:Name`))],
  surname:[b => b.notEmpty(), validatorBuilders.string("surname", () => i18next.t(`subscriptionValidators:Surname`))],
  // phoneNumber:[b => b.isPhone(), validatorBuilders.string("phoneNumber", () => i18next.t(`subscriptionValidators:PhoneNumber`))],
  // email:[b => b.isEmail(), validatorBuilders.string("email")],
  // gender:[b => b.fromPromise(async(state,value) => state.gender == undefined ? true : false)
  //   .or(
  //     b.matches(/^(male)|(female)|(other)$/)
  //   ), validatorBuilders.string("gender", () => i18next.t(`subscriptionValidators:Gender`))],
  // address:[b => b.notEmpty(), validatorBuilders.string("address", () => i18next.t(`subscriptionValidators:Address`))],
  // postCode:[b => b.matches(/^\d{4}\s*\w{2}$/), validatorBuilders.string("postCode", () => i18next.t(`subscriptionValidators:Postcode`))],
  // city:[b => b.notEmpty(), validatorBuilders.string("city", () => i18next.t(`subscriptionValidators:City`))],
})

export type PersonalInfoState = {
  input:PersonalInfo
  countryState:CountryState
  languageState:LanguageState
  jobState:JobState
  validator:Validator<PersonalInfo, PersonalInfo>,
  validatorResult:ValidatorResult
  isValid(ignoreLanguage:boolean):boolean,
}

export const initialPersonalInfoState = (userData?:UserData) : PersonalInfoState => {
  let initialPersonalInfo:PersonalInfo = emptyPersonalInfo

  if (userData != undefined) {
    let ensureAvailable = <k extends keyof UserData>(k:k, f:(_:UserData[k]) => any) => {
      if (userData[k] == null || userData[k] == undefined) return undefined
      return f(userData[k])
    }
    let id = <a,>(x:a) : a => x

    initialPersonalInfo = {
      ...initialPersonalInfo,
      name:ensureAvailable("Name", id),
      surname:ensureAvailable("Surname", id),
      // email:ensureAvailable("Email", id),
      gender:ensureAvailable("Gender", g => g.toLowerCase() == "male" ? "male" : g.toLowerCase() == "female" ? "female" : "other"),
      phoneNumber:ensureAvailable("PhoneNumber", id),
      dateOfBirth:ensureAvailable("Birthday", b => new Date(b)),
      graduationYear:ensureAvailable("ExpectedGraduationYear", id)
    }
  }

  return ({
    input:initialPersonalInfo,
    countryState:initialCountryState(userData?.Country?.Id),
    languageState:initialLanguageState(userData),
    jobState:initialJobState(userData),
    validator:personalInfoValidator.setValueToValidate(initialPersonalInfo,initialPersonalInfo) as Validator<PersonalInfo,PersonalInfo>,
    validatorResult:validatorResultBuilders.validating(""),
    isValid:function(this:PersonalInfoState, ignoreLanguage:boolean) { return isValidatorPassed(this.validatorResult) && this.countryState.isValid() && this.jobState.isValid() && (ignoreLanguage || this.languageState.isValid()) }
  })
}

// export const fieldUpdater = <s>() => <k extends keyof s>(k:k) => 
//   { [k]:(u:Updater<s[k]>) : Updater<s> => s => ({...s, [k]:u(s[k])}) }

export const personalInfoStateUpdaters = {
  input:(u:Updater<PersonalInfoState["input"]>) : Updater<PersonalInfoState> =>
    s => {
      const newInput = u(s.input)
      return ({...s, input:newInput, validator:s.validator.setValueToValidate(newInput, newInput)})
    },
  countryState:(u:Updater<PersonalInfoState["countryState"]>) : Updater<PersonalInfoState> =>
    s => ({...s, countryState:u(s.countryState)}),
  languageState:(u:Updater<PersonalInfoState["languageState"]>) : Updater<PersonalInfoState> =>
    s => ({...s, languageState:u(s.languageState)}),
  jobState:(u:Updater<PersonalInfoState["jobState"]>) : Updater<PersonalInfoState> =>
    s => ({...s, jobState:u(s.jobState)}),
  validation:(u:Updater<PersonalInfoState["validator"]>) : Updater<PersonalInfoState> =>
    s => {
      const newValidator = u(s.validator)
      return ({...s, validator:newValidator, validatorResult:newValidator.evaluateResult() })
    },  
}

export const personalInfoUpdaters = {
  name:(u:Updater<PersonalInfo["name"]>) : Updater<PersonalInfo> =>
    s => ({...s, name:u(s.name)}),
  surname:(u:Updater<PersonalInfo["surname"]>) : Updater<PersonalInfo> =>
    s => ({...s, surname:u(s.surname)}),
  // email:(u:Updater<PersonalInfo["email"]>) : Updater<PersonalInfo> =>
  //   s => ({...s, email:u(s.email)}),
  phoneNumber:(u:Updater<PersonalInfo["phoneNumber"]>) : Updater<PersonalInfo> =>
    s => ({...s, phoneNumber:u(s.phoneNumber)}),
  gender:(u:Updater<PersonalInfo["gender"]>) : Updater<PersonalInfo> =>
    s => ({...s, gender:u(s.gender)}),
  dateOfBirth:(u:Updater<PersonalInfo["dateOfBirth"]>) : Updater<PersonalInfo> =>
    s => ({...s, dateOfBirth:u(s.dateOfBirth)}),
  // postCode:(u:Updater<PersonalInfo["postCode"]>) : Updater<PersonalInfo> =>
  //   s => ({...s, postCode:u(s.postCode)}),
  // address:(u:Updater<PersonalInfo["address"]>) : Updater<PersonalInfo> =>
  //   s => ({...s, address:u(s.address)}),
  // city:(u:Updater<PersonalInfo["city"]>) : Updater<PersonalInfo> =>
  //   s => ({...s, city:u(s.city)}),
  graduationYear:(u:Updater<PersonalInfo["graduationYear"]>) : Updater<PersonalInfo> =>
    s => ({...s, graduationYear:u(s.graduationYear)}),
}
